/**
 * Seed Tag Modal Component
 *
 * Follows the same pattern as Technology and Asset sections for UI consistency.
 * Uses shared AddTag component for consistent UX across the platform.
 *
 * Key features:
 * - Supports bulk tag operations for multiple seeds
 * - Handles Asset, WebApplication, and ADObject seed types
 * - Fetches current seed data before update to prevent data loss
 * - Proper error handling with toast notifications
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { AddTag } from '@/sections/AddTag';
import { extractSeedFromResponse } from '@/sections/seeds/utils/extractSeedFromResponse';
import { Seed } from '@/types';
import { useMutation } from '@/utils/api';
import {
  tagAddedError,
  tagAddedSuccess,
  tagValidationError,
} from '@/utils/tagToastMessages';
import { validateTagInput } from '@/utils/tagValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  seedKeys: string[];
  currentTags?: string[]; // Deprecated: No longer used, always fetches from backend
  onSuccess?: () => void;
}

interface TagOperationResult {
  succeeded: number;
  failed: number;
  duplicates: number;
  results: Array<{ key: string; skipped?: boolean; reason?: string }>;
}

/**
 * Modal component for adding tags to seeds
 *
 * Uses shared AddTag component for consistent UX across the platform.
 * Handles both single seed (from drawer) and bulk operations (from table).
 *
 * Note: Always fetches current seed data from backend to prevent data loss.
 * The currentTags prop is deprecated and no longer used.
 *
 * Example usage:
 * ```tsx
 * <SeedTag
 *   open={modalState.tagModal}
 *   onClose={modalHandlers.closeTagModal}
 *   seedKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const SeedTag = ({
  open,
  onClose,
  seedKeys,
  currentTags: _currentTags = [], // Deprecated: No longer used, always fetches from backend
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutate: updateSeeds, status: updatingSeeds } = useMutation<
    TagOperationResult,
    Error,
    string
  >({
    defaultErrorMessage: 'Failed to add tag',
    mutationFn: async (newTag: string) => {
      const promises = seedKeys.map(async seedKey => {
        try {
          // ALWAYS fetch current seed to get tags AND status
          // Backend requires status to be included when updating tags
          const currentSeed = await axios.get(`/my`, {
            params: {
              key: seedKey,
              exact: true,
            },
          });

          const seed = extractSeedFromResponse(currentSeed.data);

          if (!seed) {
            throw new Error(`Seed not found: ${seedKey}`);
          }

          const typedSeed = seed as Seed;
          const existingTags = typedSeed.tags || [];
          const currentStatus = typedSeed.status || 'A';

          // Check for duplicate (case-insensitive)
          const isDuplicate = existingTags.some(
            (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
          );

          if (isDuplicate) {
            return {
              key: seedKey,
              skipped: true,
              reason: 'Tag already exists',
            };
          }

          const payload = {
            key: seedKey,
            status: currentStatus, // Include current status (required by backend)
            tags: [...existingTags, newTag],
          };

          await axios.put('/seed', payload);

          return {
            key: seedKey,
            skipped: false,
          };
        } catch (error) {
          console.error(`[SeedTagModal] Failed for ${seedKey}:`, error);
          throw error;
        }
      });

      const results = await Promise.allSettled(promises);

      // Process results
      const succeeded = results.filter(
        r => r.status === 'fulfilled' && !r.value.skipped
      ).length;

      const duplicates = results.filter(
        r => r.status === 'fulfilled' && r.value.skipped
      ).length;

      const failed = results.filter(r => r.status === 'rejected').length;

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .map((r, i) => ({ result: r, index: i }))
          .filter(
            (item): item is { result: PromiseRejectedResult; index: number } =>
              item.result.status === 'rejected'
          )
          .map(({ result, index }) => ({
            key: seedKeys[index],
            error: result.reason?.message || 'Unknown error',
          }));
        console.error('[SeedTagModal] Failed operations:', failedResults);
      }

      return {
        succeeded,
        duplicates,
        failed,
        results: results.map((r, i) => {
          if (r.status === 'fulfilled') {
            return r.value;
          }
          return { key: seedKeys[i], skipped: false };
        }),
      };
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'seed';
        },
        refetchType: 'all',
      });
    },
  });

  const handleSubmit = async (tag: string) => {
    // Validate input
    const validation = validateTagInput(tag);
    if (!validation.isValid) {
      toast.error(tagValidationError(validation.error!));
      return;
    }

    try {
      const result = await new Promise<TagOperationResult>(
        (resolve, reject) => {
          updateSeeds(validation.sanitized!, {
            onSuccess: resolve,
            onError: reject,
          });
        }
      );

      // Show appropriate feedback based on results
      if (
        result.succeeded > 0 &&
        result.failed === 0 &&
        result.duplicates === 0
      ) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagAddedSuccess('seed', count));
        onSuccess?.();
        onClose();
      } else if (
        result.succeeded > 0 &&
        (result.failed > 0 || result.duplicates > 0)
      ) {
        let message = `Tag added to ${result.succeeded} ${result.succeeded > 1 ? 'seeds' : 'seed'}`;
        if (result.duplicates > 0) {
          message += `, skipped ${result.duplicates} (already tagged)`;
        }
        if (result.failed > 0) {
          message += `, failed ${result.failed}`;
        }
        toast.warning(message);
        onSuccess?.();
        onClose();
      } else if (result.duplicates === seedKeys.length) {
        toast.warning('All selected seeds already have this tag');
        onClose();
      } else {
        toast.error(tagAddedError('seed'));
      }
    } catch (error: unknown) {
      console.error('[SeedTagModal] Tag operation failed:', error);
      // Error toast already shown in onError
    }
  };

  return (
    <AddTag
      open={open}
      onClose={onClose}
      disabled={updatingSeeds === 'pending'}
      onSubmit={handleSubmit}
      description="Tags help organize and classify seeds in your attack surface."
    />
  );
};
